/**
 * Analytics forwarding module for CADENCE SDK.
 *
 * Detects GA4 (window.dataLayer) and Segment (window.analytics) at call time
 * (not init time) to handle async third-party script loading. Each provider
 * has isolated error handling so a failure in one never suppresses the other.
 *
 * No external dependencies — pure window globals only.
 */

export interface ForwardingPayload {
  experimentId: string
  experimentName: string
  variantGroupLabel: string // "Control", "A", "B", "C", or "D"
}

/**
 * Forward experiment assignment data to all detected analytics providers.
 * Calls GA4 and Segment forwarding independently — an error in one provider
 * does not suppress the other.
 */
export function forwardToAnalytics(payload: ForwardingPayload): void {
  forwardToGA4(payload)
  forwardToSegment(payload)
}

/**
 * Push experiment data to GA4 via window.dataLayer.
 *
 * Uses defensive initialization (window.dataLayer = window.dataLayer || [])
 * so events are buffered even if GTM hasn't loaded yet — GTM drains the array
 * when its container initializes.
 *
 * GA4 schema: experiment_id + experiment_variant (low-cardinality group label).
 * experiment_name is intentionally omitted to avoid GA4 custom dimension
 * cardinality explosion (see Pitfall 6 in research).
 */
function forwardToGA4(payload: ForwardingPayload): void {
  try {
    if (typeof window === 'undefined') return

    // Defensive init: ensures array exists even if GTM hasn't loaded
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).dataLayer = (window as any).dataLayer || []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).dataLayer.push({
      event: 'experiment_viewed',
      experiment_id: payload.experimentId,
      experiment_variant: payload.variantGroupLabel,
      // experiment_name intentionally omitted — high-cardinality, use experiment_id
      // to cross-reference with the CADENCE dashboard
    })
  } catch (err) {
    console.warn('[CADENCE] GA4 forwarding error:', err)
  }
}

/**
 * Track experiment assignment via Segment analytics.track().
 *
 * Checks for window.analytics and its track method at call time (not init time)
 * to handle Segment's async loading pattern. The analytics.js CDN snippet
 * installs a stub that queues calls before the library loads.
 *
 * experiment_name is included in Segment (unlike GA4) because Segment has
 * no cardinality limits on event properties.
 *
 * nonInteraction: 1 prevents bounce rate inflation in GA4 Segment destination.
 */
function forwardToSegment(payload: ForwardingPayload): void {
  try {
    if (typeof window === 'undefined') return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const analytics = (window as any).analytics
    if (!analytics || typeof analytics.track !== 'function') return

    analytics.track('Experiment Viewed', {
      experiment_id: payload.experimentId,
      experiment_name: payload.experimentName,
      variation_name: payload.variantGroupLabel,
      nonInteraction: 1,
    })
  } catch (err) {
    console.warn('[CADENCE] Segment forwarding error:', err)
  }
}

// Module-level flag: ensures analytics.page is only patched once across all
// getVariant() calls in a session
let segmentPagePatched = false

/**
 * Attach experiment context to Segment analytics.page() calls.
 *
 * Uses Segment's addSourceMiddleware API (preferred, available in analytics-next)
 * or falls back to monkey-patching analytics.page for classic analytics.js.
 *
 * Injects a cadence_experiments property into every subsequent page() call,
 * formatted as a comma-separated string of "experimentId:variantGroupLabel" pairs.
 *
 * Only patches once per session — subsequent calls update the experiments array
 * in the existing patch closure via reference to the passed array.
 *
 * @param experiments - Array of all active experiment assignments for this session
 */
export function attachSegmentPageContext(
  experiments: Array<{ experimentId: string; variantGroupLabel: string }>
): void {
  try {
    if (typeof window === 'undefined') return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const analytics = (window as any).analytics
    if (!analytics || typeof analytics.page !== 'function') return

    // Only patch once — subsequent calls pass updated experiments array by reference
    if (segmentPagePatched) return
    segmentPagePatched = true

    if (typeof analytics.addSourceMiddleware === 'function') {
      // Preferred: analytics-next (Segment's modern analytics library)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      analytics.addSourceMiddleware(({ payload, next }: any) => {
        if (payload.type() === 'page') {
          const props = payload.obj.properties || {}
          props.cadence_experiments = experiments
            .map((e) => `${e.experimentId}:${e.variantGroupLabel}`)
            .join(',')
          payload.obj.properties = props
        }
        next(payload)
      })
    } else {
      // Fallback: classic analytics.js monkey-patch
      const originalPage = analytics.page.bind(analytics)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      analytics.page = function (...args: any[]) {
        // analytics.page(category?, name?, properties?, options?, callback?)
        // Find the properties argument: first object that isn't a string/function
        const propsIdx = args.findIndex(
          (arg, i) => i > 0 && typeof arg === 'object' && arg !== null && !Array.isArray(arg)
        )
        if (propsIdx >= 0) {
          args[propsIdx].cadence_experiments = experiments
            .map((e) => `${e.experimentId}:${e.variantGroupLabel}`)
            .join(',')
        }
        return originalPage(...args)
      }
    }
  } catch (err) {
    console.warn('[CADENCE] Segment page context error:', err)
  }
}
